/** @file Paints the curve-graph background: value grid with labels, frame grid, frame ruler strip. */
import { timelineScale } from '@pose2d/editor-geometry/timelineScale';
import type { CurveCanvas, CurveFrame, CurveStyle } from './curveCanvasTypes';
import { CURVE_GRAPH } from './curveGraph.data';
import { curveTickStep } from './curveTickStep';
import { curveValueScale } from './curveValueScale';

/** Shortest label of a tick value (no float noise such as 0.30000000000000004). */
const label = (v: number): string => String(Number(v.toPrecision(6)));

function frameTicks(ctx: CurveCanvas, frame: CurveFrame): void {
  const time = frame.axes.time;
  const step = curveTickStep(time.pxPerFrame, CURVE_GRAPH.minTickPx, true);
  const [first, last] = timelineScale.visibleFrames(time, frame.width);
  for (let f = Math.ceil(first / step) * step; f <= last; f += step) {
    const x = Math.round(timelineScale.frameToX(time, f)) + 0.5;
    ctx.moveTo(x, CURVE_GRAPH.rulerPx);
    ctx.lineTo(x, frame.height);
    ctx.fillText(String(f), x + 3, CURVE_GRAPH.rulerPx - 5);
  }
}

function valueTicks(ctx: CurveCanvas, frame: CurveFrame): void {
  const value = frame.axes.value;
  const step = curveTickStep(value.pxPerUnit, CURVE_GRAPH.minTickPx / 2, false);
  const top = curveValueScale.toValue(value, CURVE_GRAPH.rulerPx);
  const bottom = curveValueScale.toValue(value, frame.height);
  for (let v = Math.ceil(bottom / step) * step; v <= top; v += step) {
    const y = Math.round(curveValueScale.toY(value, v)) + 0.5;
    ctx.moveTo(0, y);
    ctx.lineTo(frame.width, y);
    ctx.fillText(label(v), 3, y - 3);
  }
}

/** Background, ruler strip, then one stroked path holding every grid line, labels in the text colour. */
export function drawCurveGrid(ctx: CurveCanvas, frame: CurveFrame, style: CurveStyle): void {
  ctx.fillStyle = style.background;
  ctx.fillRect(0, 0, frame.width, frame.height);
  ctx.fillStyle = style.ruler;
  ctx.fillRect(0, 0, frame.width, CURVE_GRAPH.rulerPx);
  ctx.font = style.font;
  ctx.fillStyle = style.text;
  ctx.strokeStyle = style.grid;
  ctx.lineWidth = 1;
  ctx.setLineDash([]);
  ctx.beginPath();
  frameTicks(ctx, frame);
  valueTicks(ctx, frame);
  ctx.stroke();
}
